import clsx from "clsx";
import { useEffect, useMemo, useState } from "preact/hooks";
import useUserDevice from "../../hooks/useUserDevice";
import Logo8thWall from "../../assets/8thwall.svg";
import { Spinner } from "../../components/Spinner";
import { QrCodeGenerator } from "../../components/QrCodeGenerator";
import { useTimeout } from "../../hooks/useTimeout";
import useUnlockAudio from "@/hooks/useUnlockAudio";
import { ModalOverlay } from "@/components/Modal/ModalOverlay";
import BgMobile from "../../assets/splash-bg.png";
import BgDesktop from "../../assets/desktop-bg.png";
import BgDesktopRight from "../../assets/desktop-bg-right.png";
import Lockup from "../../assets/cba-logo.png";
import useScreenOrientation from "@/hooks/useScreenOrientation";
import { CopyToClipboardTrigger } from "./CopyToClipboardTrigger";
import ClipboardImg from "../../assets/clipboard.svg";

type SplashProps = {
  onPermissionsGranted: () => void;
};
const params = new URLSearchParams(window.location.search);
const noAR = params.get("noAR") === "1";

export function Splash({ onPermissionsGranted }: SplashProps) {
  const [pageType, setPageType] = useState<"loading" | "default">("loading");
  const [uiType, setUiType] = useState<"loading" | "default">("default");
  const [showModalOverlay, setShowModalOverlay] = useState(false);
  const { device } = useUserDevice();
  useUnlockAudio("ej-video");
  const [permissionDenied, setPermissionDenied] = useState<
    "camera-denied" | "motion-denied" | undefined
  >(undefined);

  useEffect(() => {
    let isMounted = true;

    const fontPromise = document?.fonts?.ready || Promise.resolve();
    const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 2000));

    Promise.all([fontPromise, timeoutPromise]).then(() => {
      if (isMounted) {
        setPageType("default");
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const requestCameraAccess = () => {
    setShowModalOverlay(false);
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: false })
      .then(() => {
        onPermissionsGranted();
        setUiType("loading");
      })
      .catch((err) => {
        setPermissionDenied("camera-denied");
        console.log("Error: camera rejected." + err);
      });
  };

  const checkCameraPermissions = () => {
    setShowModalOverlay(false);
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: "camera" })
        .then((permissionStatus) => {
          if (permissionStatus.state === "granted") {
            onPermissionsGranted();
            setUiType("loading");
          } else if (
            permissionStatus.state === "prompt" ||
            permissionStatus.state === "denied"
          ) {
            if (device === "iOS") {
              requestCameraAccess();
            } else {
              onPermissionsGranted();
              setUiType("loading");
            }
          }
        })
        .catch((err) => {
          console.log("Error checking camera permissions:", err);
          if (device === "iOS") {
            requestCameraAccess();
          } else {
            onPermissionsGranted();
            setUiType("loading");
          }
        });
    } else {
      if (device === "iOS") {
        requestCameraAccess();
      } else {
        onPermissionsGranted();
        setUiType("loading");
      }
    }
  };

  const requestMotion = () => {
    setShowModalOverlay(true);
    if (
      typeof DeviceMotionEvent !== "undefined" &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      typeof (DeviceMotionEvent as any).requestPermission === "function"
    ) {
      // motion avaiable
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (DeviceMotionEvent as any)
        .requestPermission()
        .then((response: string) => {
          if (response === "granted") {
            checkCameraPermissions();
          } else {
            console.log(response);
            setPermissionDenied("motion-denied");
          }
        })
        .catch((err: Error) => {
          setPermissionDenied("motion-denied");
          console.log("Error: motion rejected." + err);
        });
    } else {
      checkCameraPermissions();
    }
  };

  // useTimeout(() => {
  //   setPageType("default");
  // }, 2000);

  const deviceType = useMemo(() => {
    if (device === undefined) {
      return "loading";
    } else if (device === "iOS" || device === "Android") {
      return "mobile";
    } else return noAR ? "mobile" : "desktop";
    return "mobile";
  }, [device]);

  return (
    <div
      className={clsx(
        "bg-cover bg-center h-full w-full bg-white text-black relative xpointer-events-none overflow-hidden items-center xpx-5 flex flex-col"
      )}
    >
      {deviceType !== "desktop" && (
        <>
          <div className="flex flex-col gap-4 items-center h-full relative w-full">
            <div
              className={clsx(
                "relative xflex flex-col items-center basis-1/2 px-5 animate-fade-in w-full max-w-[550px] mx-auto "
              )}
            >
              <div
                className={clsx(
                  "absolute xflex flex-col xw-full xh-full transition-all transform duration-700",
                  {
                    "left-1/2 -translate-x-1/2": pageType === "loading",
                    "left-5": pageType === "default",
                  }
                )}
              >
                <img
                  src={Lockup}
                  className={clsx("transition-all transform duration-700 ", {
                    "relative max-w-[80px] pt-[25px] justify-end":
                      pageType === "default",
                    "relative pt-[130px] max-w-[200px]": pageType === "loading",
                  })}
                />
              </div>
              {pageType === "default" && (
                <div
                  className={clsx(
                    "flex flex-col justify-end gap-[27px] pb-5 w-full h-full animate-delayed-fade-in opacity-0"
                  )}
                >
                  <h2 className=" font-cba-sans-bold text-[43px] leading-[40px]">
                    General
                    <br />
                    Insurance <br /> Reimagined
                  </h2>
                  <p className="font-source-serif-pro text-[28px] leading-[30px] italic">
                    AR Experience
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-col items-center basis-1/2 w-full">
              {deviceType === "loading" && (
                <div className="flex items-end justify-center flex-grow basis-1/2 pb-20">
                  <Spinner size="md" variant="black" />
                </div>
              )}
              {deviceType === "mobile" &&
                (permissionDenied ? (
                  <>
                    <div className="flex items-center flex-grow basis-1/2 pb-20">
                      <div className="animate-fade-in">
                        <PermissionsDeniedScreen
                          permissionDenied={permissionDenied}
                        />
                      </div>

                      <div className="animate-fade-in absolute bottom-0 pb-4 left-1/2 -translate-x-1/2">
                        <img src={Logo8thWall} alt="8th Wall" />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="h-full flex w-full">
                    {pageType === "loading" && (
                      <div className="flex items-end flex-grow basis-1/2 pb-20 justify-center">
                        <div className="xanimate-fade-in">
                          <Spinner size="md" variant="black" />
                        </div>

                        <div className="animate-fade-in absolute bottom-0 pb-4 left-1/2 -translate-x-1/2">
                          <img src={Logo8thWall} alt="8th Wall" />
                        </div>
                      </div>
                    )}

                    {pageType === "default" && (
                      <>
                        <div
                          className={clsx(
                            "flex flex-col relative items-center flex-grow basis-1/2 justify-end pb-12 animate-fade-in w-full h-full "
                          )}
                        >
                          <img
                            className="pointer-events-none absolute bottom-0 left-0 w-screen h-full sm:h-auto object-contain object-bottom animate-slide-up -z-10 "
                            src={BgMobile}
                          />
                          {/* {uiType === "loading" && (
                        
                           
                            <div className="">
                              <Spinner size="md" variant="black" />
                            </div>
                        )} */}
                          {/* {uiType === "default" && ( */}

                          <div className="px-5 flex flex-col items-center w-full">
                            <h2 className="text-lg xs:text-xl items-end text-center leading-[25px] xs:leading-[28px] max-w-[320px]">
                              Access to your camera, motion & orientation is
                              required
                            </h2>
                            <br />
                            <button
                              className="relative max-w-[210px] w-full h-[50px] border text-[#3A3A3A] text-base font-cba-sans-extrabold border-black rounded-full active:opacity-70 "
                              onClick={requestMotion}
                              disabled={uiType === "loading"}
                            >
                              {uiType === "default" ? (
                                "Begin"
                              ) : (
                                <span className="opacity-70 pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                  <Spinner size="sm" variant="black" />
                                </span>
                              )}
                            </button>
                          </div>
                          {/* )} */}
                        </div>
                        {showModalOverlay && <ModalOverlay>''</ModalOverlay>}
                      </>
                    )}
                  </div>
                ))}
            </div>
          </div>
          {/* </div> */}
        </>
      )}

      {deviceType === "desktop" && (
        <>
          <DesktopSplash />
        </>
      )}
    </div>
  );
}

function DesktopSplash() {
  // const url = useEJXLaunchUrl();
  const url = window.location.href;
  const screenOrientation = useScreenOrientation();

  return (
    <>
      <div className="absolute h-full xw-[20%] left-0">
        <img
          className="pointer-events-none object-cover object-right xabsolute xbottom-0 h-full w-full "
          src={BgDesktop}
        />
      </div>
      <div className="absolute h-[50%] right-0 bottom-0 xw-[20%]">
        <img
          className="pointer-events-none object-cover xobject-right xabsolute xbottom-0 h-full w-full "
          src={BgDesktopRight}
        />
      </div>
      <img
        src={Lockup}
        className="absolute right-10 top-10 md:right-20 md:top-20 max-w-[90px] md:max-w-[110px] w-full h-auto "
      />
      <div
        className={clsx(
          "overflow-y-scroll flex xjustify-center items-center gap-10 h-full w-full relative z-10 xp-10 flex-col pointer-events-auto"
        )}
      >
        <div className="flex flex-col justify-center items-center xpb-5 w-full animate-fade-in relative md-lg:absolute top-[40px] left-[40px]">
          <div className="flex flex-col xitems-center gap-[27px] w-full ">
            <h2 className="text-left font-cba-sans-bold xl:text-[60px] xl:leading-[57px] text-[42px] leading-[40px]">
              General
              <br />
              Insurance <br /> Reimagined
            </h2>
            <p className="text-left font-source-serif-pro xl:text-[38px] xl:leading-[42px] text-[26px] leading-[29px] italic">
              AR Experience
            </p>
          </div>
        </div>
        <div
          className={clsx(
            "md-lg:overflow-auto flex flex-col items-center justify-center w-full py-10 xmd-lg:pb-0 flex-grow xl:absolute xl:top-1/2 xl:-translate-y-[40%] relative",
            {
              // 'absolute top-1/2 -translate-y-1/2': screenOrientation === 'landscape'
            }
          )}
        >
          <QrCodeGenerator value={url} fgColor="#000000" size={250} />
          <div className="xpx-5 flex flex-col items-center w-full px-5">
            <h2 className="text-base xs:text-lg items-end text-center leading-[22px] xs:leading-[25px] pt-5 pb-12">
              Scan the QR code to launch the <br />
              General Insurance Reimagined AR Experience
            </h2>
            <CopyToClipboardTrigger text={window.location.href}>
              <div className="flex justify-center items-center relative max-w-[210px] w-full h-[50px] border text-[#3A3A3A] text-base font-cba-sans-extrabold border-black rounded-full active:opacity-70">
                <span className="text-center max-w-[310px] whitespace-nowrap overflow-hidden text-ellipsis w-full">
                  Copy Link
                </span>
                <img
                  className="cursor-pointer absolute right-3"
                  src={ClipboardImg}
                />
              </div>
            </CopyToClipboardTrigger>
            {/* <button className="relative max-w-[210px] w-full h-[50px] border text-[#3A3A3A] text-base font-cba-sans-extrabold border-black rounded-full active:opacity-70">
              Begin
            </button> */}
          </div>
        </div>
      </div>
    </>
  );
}

type PermissionsDeniedScreenProps = {
  permissionDenied: "camera-denied" | "motion-denied" | undefined;
};
function PermissionsDeniedScreen({
  permissionDenied,
}: PermissionsDeniedScreenProps) {
  return (
    <div className="flex flex-col gap-5 text-black px-5">
      {permissionDenied === "camera-denied" && (
        <>
          <h2 className="font-cba-sans text-lg text-center">
            Camera access is required.
          </h2>
          <h2 className="font-cba-sans text-lg text-center">
            Quit and open the browser again.
          </h2>
        </>
      )}
      {permissionDenied === "motion-denied" && (
        <>
          <h2 className="font-cba-sans text-lg text-center ">
            Motion and orientation access is required.
          </h2>
          <h2 className="font-cba-sans text-lg text-center ">
            Quit and open the browser again.
          </h2>
        </>
      )}
    </div>
  );
}
